import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { fmtDate } from "@/lib/api";
import { Plus, BookOpen, CheckCircle2, Clock, Trash2, Pencil, ShieldCheck, Users2, FileDown, GraduationCap } from "lucide-react";

const CATS = ["Operations", "Health & Safety", "Gas Handling", "POS & Cash Handling", "Procurement", "HR & Conduct", "Compliance", "Quality Control", "IT & Security"];
const STATUS_COLOR = { Draft: "border-zinc-400 text-zinc-600", Published: "border-green-600 text-green-700", Archived: "border-zinc-300 text-zinc-400" };
const empty = { title: "", category: "", content: "", effective_date: "", review_date: "", status: "Draft", is_onboarding: false };

export default function SOPs() {
  const { can } = useAuth();
  const confirm = useConfirm();
  const editable = can("sops");
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [reader, setReader] = useState(null);
  const [compliance, setCompliance] = useState(null);

  const load = () => {
    const params = cat !== "all" ? { category: cat } : {};
    api.get("/sops", { params }).then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load SOPs"));
  };
  useEffect(() => { load(); }, [cat]);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setEditing(s.id); setOpen(true); };

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    try {
      if (editing) await api.put(`/sops/${editing}`, form);
      else await api.post("/sops", form);
      toast.success(editing ? "SOP updated" : "SOP created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (s) => {
    if (!(await confirm({ title: "Delete SOP?", description: `"${s.title}" will be removed.`, confirmText: "Delete" }))) return;
    await api.delete(`/sops/${s.id}`); toast.success("Deleted"); load();
  };
  const setStatus = async (s, status) => { await api.patch(`/sops/${s.id}/status`, { status }); toast.success(status === "Published" ? "Published" : status); load(); };
  const acknowledge = async (s) => {
    try { await api.post(`/sops/${s.id}/acknowledge`); toast.success("Acknowledged — thank you"); setReader(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const viewCompliance = async (s) => { const { data } = await api.get(`/sops/${s.id}/acknowledgements`); setCompliance({ sop: s, ...data }); };

  const shown = items;

  return (
    <div>
      <PageHeader title="SOPs & Compliance" subtitle="Standard Operating Procedures with per-version staff sign-off.">
        {editable && <Button onClick={openNew} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="new-sop-button"><Plus className="h-4 w-4 mr-1.5" /> New SOP</Button>}
      </PageHeader>

      <div className="mb-4">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[240px] h-10 bg-white" data-testid="sop-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((s) => (
          <div key={s.id} className="bg-white border border-border rounded-md p-5 flex flex-col group" data-testid={`sop-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-sm bg-black text-white flex items-center justify-center"><BookOpen className="h-5 w-5" /></div>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="text-[10px]">v{s.version}</Badge>
                <Badge variant="outline" className={STATUS_COLOR[s.status]}>{s.status}</Badge>
              </div>
            </div>
            <div className="mt-3 font-display font-semibold leading-tight flex-1">{s.title}</div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">{s.category || "General"}{s.is_onboarding && <span className="inline-flex items-center gap-1 text-primary font-medium"><GraduationCap className="h-3 w-3" /> New Starter</span>}</div>
            <div className="mt-3 flex items-center gap-2">
              {s.status === "Published" && (s.acknowledged
                ? <span className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Acknowledged</span>
                : <span className="text-xs text-primary flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Sign-off required</span>)}
              <Button size="sm" variant="outline" className="ml-auto h-8" onClick={() => setReader(s)} data-testid={`read-sop-${s.id}`}>Read</Button>
            </div>
            {editable && (
              <div className="mt-3 pt-3 border-t border-border flex items-center gap-1">
                {s.status !== "Published" && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setStatus(s, "Published")} data-testid={`publish-sop-${s.id}`}>Publish</Button>}
                {s.status === "Published" && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setStatus(s, "Archived")} data-testid={`archive-sop-${s.id}`}>Archive</Button>}
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => viewCompliance(s)} data-testid={`compliance-sop-${s.id}`}><Users2 className="h-3.5 w-3.5 mr-1" /> Compliance</Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" title="Export PDF" onClick={() => window.open(`${API}/sops/${s.id}/pdf`, "_blank")} data-testid={`pdf-sop-${s.id}`}><FileDown className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 ml-auto" onClick={() => openEdit(s)} data-testid={`edit-sop-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => del(s)} data-testid={`delete-sop-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </div>
        ))}
        {shown.length === 0 && <p className="col-span-full text-center py-12 text-muted-foreground">No SOPs yet.</p>}
      </div>

      {/* Create/edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit SOP" : "New SOP"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <F label="Title" cn="col-span-2"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sop-title-input" /></F>
            <F label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="sop-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Draft", "Published", "Archived"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Effective Date"><DatePicker value={form.effective_date} onChange={(v) => setForm({ ...form, effective_date: v })} placeholder="Select effective date" testid="sop-effective-date" /></F>
            <F label="Next Review Date"><DatePicker value={form.review_date} onChange={(v) => setForm({ ...form, review_date: v })} placeholder="Select review date" testid="sop-review-date" /></F>
            <F label="Procedure Content" cn="col-span-2"><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} placeholder={"Purpose, scope and step-by-step procedure...\n1. ...\n2. ..."} data-testid="sop-content-input" /></F>
            {editing && <p className="col-span-2 text-xs text-muted-foreground">Editing the content publishes a new version and re-requires staff sign-off.</p>}
            <label className="col-span-2 flex items-center gap-2.5 cursor-pointer border border-border rounded-md px-3 py-2.5" data-testid="sop-onboarding-toggle">
              <input type="checkbox" checked={!!form.is_onboarding} onChange={(e) => setForm({ ...form, is_onboarding: e.target.checked })} className="h-4 w-4 accent-[#E51921]" />
              <span className="text-sm">Include in the <b>New Starter Pack</b> (every new employee must sign off in week one)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-sop-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reader */}
      <Dialog open={!!reader} onOpenChange={() => setReader(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {reader && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" /> {reader.title}</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                <span>{reader.category}</span><span>Version {reader.version}</span>
                {reader.effective_date && <span>Effective {fmtDate(reader.effective_date)}</span>}
                {reader.review_date && <span>Review by {fmtDate(reader.review_date)}</span>}
              </div>
              <div className="mt-3 text-sm whitespace-pre-wrap leading-relaxed border-l-2 border-border pl-4 py-1 max-h-[50vh] overflow-y-auto">
                {reader.content || "No content."}
              </div>
              <DialogFooter className="items-center">
                {reader.status === "Published" ? (
                  reader.acknowledged
                    ? <span className="text-sm text-green-700 flex items-center gap-1.5 mr-auto"><CheckCircle2 className="h-4 w-4" /> You have acknowledged this version</span>
                    : <><span className="text-xs text-muted-foreground mr-auto">By acknowledging you confirm you have read and understood this SOP.</span>
                        <Button onClick={() => acknowledge(reader)} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="acknowledge-sop-button"><ShieldCheck className="h-4 w-4 mr-1.5" /> I Acknowledge</Button></>
                ) : <span className="text-xs text-muted-foreground mr-auto">This SOP is not published yet.</span>}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Compliance */}
      <Dialog open={!!compliance} onOpenChange={() => setCompliance(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {compliance && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl">Sign-off — {compliance.sop.title}</DialogTitle></DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="text-3xl font-display font-bold tabular">{compliance.compliance}%</div>
                <div className="text-sm text-muted-foreground">{compliance.acknowledged} of {compliance.total} staff acknowledged v{compliance.version}</div>
              </div>
              <div className="divide-y divide-border border border-border rounded-md">
                {compliance.rows.map((r) => (
                  <div key={r.user_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="flex-1">{r.name} <span className="text-xs text-muted-foreground capitalize">({r.role?.replace("_", " ")})</span></span>
                    {r.acknowledged
                      ? <span className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {(r.acknowledged_at || "").slice(0, 10)}</span>
                      : <Badge variant="outline" className="text-primary border-primary text-[10px]">Pending</Badge>}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, cn = "" }) {
  return <div className={`space-y-1.5 ${cn}`}><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}
